// src/portal-app/insights/kit.js
//
// W2 Task 5 — the shared presentation kit the four Insights surfaces
// (Financial, Operational, Product, Cost — Tasks 6-9) are assembled from.
// EVERYTHING here is presentation-only: no data fetching, no services, no
// AuthContext. Currency arrives as a symbol-string prop (surfaces read it
// from useAuth().getCurrency()); the only context used is LanguageContext
// for t()/language/RTL.
//
// The kit's main job beyond looks is absorbing the reporting_v2 endpoints'
// inconsistencies ONCE, in normaliseKpi(), so the surfaces never re-derive:
//   • percent KPIs are marked three different ways —
//       /financial  → is_percentage: true
//       /products   → unit: '%'
//       /costs      → neither; only the name carries it ("Gross Margin %")
//   • /financial's "Profit Margin" change_percent is a POINT delta, not a
//     relative % (backend builds it by hand as curr − prev)
//   • /operations' "Peak Hour" value is a STRING with null comparisons
//   • change_percent is null whenever the previous period was 0 or None —
//     rendered "—", never "0%"
//   • invert: true (COGS, Operating Expenses, Revenue Concentration) means
//     DOWN IS GOOD — a fall renders as the good/green treatment
//
// Charting rules (ui-ux-pro-max, hard requirements):
//   trend → line chart only at ≥4 points, else a stat card;
//   ranking → horizontal BarList, value labels ALWAYS visible (AAA),
//     ≤15 rows then "+N more";
//   part-to-whole → StackedShareBar + legend carrying numeric values
//     (deliberately replaces the web pages' pie charts — pre-approved);
//   KPI vs target → bullet ProgressVsTarget, value AND % of target as text;
//   no horizontal scrolling of content — the RangeBar chip strip is the one
//   deliberate, self-contained exception.
//
// A11y/RTL: tokens only (--c-*), tabular numerals on every figure, inline
// SVG only, role="img" + aria-label on charts, logical properties
// throughout (bars grow from inline-start), reduced-motion respected.
import React, { useMemo, useState } from 'react';
import { GlassCard } from '../shell/LiquidGlass';
import { useLanguage } from '../../context/LanguageContext';
import { PRESETS, rangeForPreset, formatRangeLabel, toLocalISO } from '../nav/dateRange';

/* ------------------------------------------------------------------ */
/* Stylesheet — injected once (LiquidGlass ensureCss pattern).         */
/* ------------------------------------------------------------------ */
const CSS = `
.insk-num{font-variant-numeric:tabular-nums;}
.insk-label{font-size:11px;font-weight:600;letter-spacing:.9px;text-transform:uppercase;
  color:var(--c-textLight);}

/* ---- RangeBar ---- */
.insk-rangebar{margin-bottom:14px;}
.insk-chips{display:flex;gap:8px;overflow-x:auto;padding:2px;margin:-2px;
  -webkit-overflow-scrolling:touch;scrollbar-width:none;}
.insk-chips::-webkit-scrollbar{display:none;}
.insk-chip{flex:none;display:inline-flex;align-items:center;gap:6px;
  min-height:44px;padding:0 16px;border-radius:999px;cursor:pointer;
  font-family:inherit;font-size:13px;font-weight:600;white-space:nowrap;
  color:var(--c-textLight);
  background:color-mix(in srgb,var(--c-surface) 38%,transparent);
  border:1px solid color-mix(in srgb,var(--c-surface) 50%,transparent);
  -webkit-backdrop-filter:blur(12px);backdrop-filter:blur(12px);
  transition:transform .15s ease,background .15s ease;}
.insk-chip:active{transform:scale(.96);}
.insk-chip-active{color:var(--c-white);font-weight:700;
  background:var(--c-green);border-color:var(--c-green);}
.insk-chip svg{width:13px;height:13px;}
.insk-range-label{margin:9px 6px 0;font-size:12px;font-weight:600;
  color:var(--c-textLight);}

/* ---- HeroValue ---- */
.insk-hero{padding:20px 22px 22px;margin-bottom:14px;}
.insk-hero-row{display:flex;align-items:center;justify-content:space-between;gap:10px;}
.insk-hero-big{margin-top:10px;font-size:38px;font-weight:800;letter-spacing:-1.2px;
  color:var(--c-ink);line-height:1;}
.insk-hero-big sup{font-size:20px;font-weight:700;color:var(--c-textLight);top:-12px;
  position:relative;letter-spacing:-.5px;}
.insk-hero-big sup.insk-hero-dec{font-size:18px;}
.insk-hero-sub{margin-top:9px;font-size:12px;font-weight:500;color:var(--c-textLight);}
.insk-hero-compare{margin-top:9px;font-size:12px;font-weight:500;color:var(--c-textLight);}
/* With a sub line above (Product Detail's category line) the compare line
   tightens up — preserves the pre-extraction spacing exactly. */
.insk-hero-sub + .insk-hero-compare{margin-top:4px;}

/* ---- KpiGrid ---- */
.insk-kpis{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:14px;}
.insk-kpi{padding:14px 15px;box-sizing:border-box;min-width:0;}
.insk-kpi-k{font-size:10.5px;font-weight:600;letter-spacing:.6px;
  text-transform:uppercase;color:var(--c-textLight);
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.insk-kpi-v{margin-top:6px;font-size:22px;font-weight:800;letter-spacing:-.6px;
  color:var(--c-ink);line-height:1.15;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
/* Text KPIs (Peak Hour "13:00 - 14:00") get their own type rule: the 22px
   nowrap numeric scale ellipsis-truncates a string on a 360dp half-width
   tile ("13:00 - 1…" reads as a truncated NUMBER). Strings wrap at a
   smaller size instead — the tile has vertical room (I1). Numeric tiles
   keep the scale above. */
.insk-kpi-v-text{font-size:17px;white-space:normal;line-height:1.2;}
.insk-kpi-foot{margin-top:7px;display:flex;align-items:center;gap:7px;flex-wrap:wrap;}
.insk-delta{display:inline-flex;align-items:center;gap:3px;
  font-size:11.5px;font-weight:700;padding:2.5px 8px;border-radius:999px;}
.insk-delta svg{width:11px;height:11px;}
.insk-delta-good{color:var(--c-success);
  background:color-mix(in srgb,var(--c-success) 12%,transparent);
  border:1px solid color-mix(in srgb,var(--c-success) 22%,transparent);}
.insk-delta-bad{color:var(--c-red);
  background:color-mix(in srgb,var(--c-red) 10%,transparent);
  border:1px solid color-mix(in srgb,var(--c-red) 22%,transparent);}
.insk-delta-flat,.insk-delta-none{color:var(--c-muted);
  background:color-mix(in srgb,var(--c-ink) 6%,transparent);
  border:1px solid color-mix(in srgb,var(--c-ink) 10%,transparent);}
.insk-kpi-cap{font-size:11px;font-weight:500;color:var(--c-textLight);}

/* ---- InsightList ---- */
.insk-ins-list{padding:6px;margin-bottom:14px;}
.insk-ins-row{display:flex;align-items:flex-start;gap:11px;padding:12px;border-radius:18px;}
.insk-ins-row + .insk-ins-row{border-top:1px solid color-mix(in srgb,var(--c-ink) 5%,transparent);}
.insk-ins-ic{width:32px;height:32px;border-radius:11px;flex:none;
  display:inline-flex;align-items:center;justify-content:center;}
.insk-ins-ic svg{width:16px;height:16px;}
.insk-ins-info .insk-ins-ic{background:color-mix(in srgb,var(--c-blue) 12%,transparent);color:var(--c-blue);}
.insk-ins-warning .insk-ins-ic{background:color-mix(in srgb,var(--c-amber) 14%,transparent);color:var(--c-amber);}
.insk-ins-success .insk-ins-ic{background:color-mix(in srgb,var(--c-success) 12%,transparent);color:var(--c-success);}
.insk-ins-opportunity .insk-ins-ic{background:color-mix(in srgb,var(--c-purple) 12%,transparent);color:var(--c-purple);}
.insk-ins-t1{font-size:13px;font-weight:700;color:var(--c-ink);}
.insk-ins-t2{margin-top:2px;font-size:12px;font-weight:500;color:var(--c-textLight);line-height:1.45;}

/* ---- BarList ---- */
.insk-bars{padding:16px 16px 12px;margin-bottom:14px;}
.insk-bar-row{padding:6px 0;}
/* Interactive rows (onItemPress) — a real full-width button (44pt target,
   touch-action per ui-ux-pro-max Tap Delay), inheriting the row anatomy so
   AAA always-visible values hold on tappable rows too. */
button.insk-bar-row{display:block;width:100%;min-height:44px;text-align:start;
  background:none;border:0;margin:0;font:inherit;color:inherit;cursor:pointer;
  border-radius:10px;touch-action:manipulation;}
button.insk-bar-row:active{background:color-mix(in srgb, var(--c-ink) 6%, transparent);}
.insk-bar-head{display:flex;align-items:baseline;justify-content:space-between;gap:10px;margin-bottom:4px;}
.insk-bar-label{font-size:13px;font-weight:600;color:var(--c-ink);min-width:0;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.insk-bar-value{flex:none;font-size:12.5px;font-weight:700;color:var(--c-ink);}
.insk-bar-pct{font-weight:600;color:var(--c-textLight);}
.insk-bar-track{height:8px;border-radius:4px;overflow:hidden;
  background:color-mix(in srgb,var(--c-ink) 7%,transparent);}
.insk-bar-fill{height:100%;border-radius:4px;}
.insk-bar-cap{margin-top:3px;font-size:11px;font-weight:500;color:var(--c-textLight);}
.insk-bar-more{display:flex;width:100%;min-height:44px;align-items:center;justify-content:center;
  margin-top:4px;border:none;border-radius:12px;cursor:pointer;font-family:inherit;
  font-size:12.5px;font-weight:700;color:var(--c-greenDark);
  background:color-mix(in srgb,var(--c-green) 8%,transparent);}

/* ---- TrendChart ---- */
.insk-trend{padding:16px 18px 12px;margin-bottom:14px;}
.insk-trend-head{display:flex;align-items:baseline;justify-content:space-between;gap:10px;margin-bottom:8px;}
.insk-trend-total{font-size:11px;font-weight:700;color:var(--c-greenDark);white-space:nowrap;
  background:color-mix(in srgb,var(--c-green) 10%,transparent);
  border:1px solid color-mix(in srgb,var(--c-green) 16%,transparent);
  padding:3px 9px;border-radius:999px;}
.insk-trend-svg{width:100%;height:auto;display:block;}
.insk-trend-x{position:relative;display:flex;justify-content:space-between;padding:4px 4px 0;
  font-size:10px;font-weight:600;color:var(--c-textLight);}
.insk-trend-xmid{position:absolute;transform:translateX(-50%);}
.insk-trend-legend{display:flex;flex-wrap:wrap;gap:6px 14px;margin-top:8px;}
.insk-trend-lg{display:inline-flex;align-items:center;gap:6px;
  font-size:11.5px;font-weight:600;color:var(--c-textLight);}
.insk-trend-lgdot{width:9px;height:9px;border-radius:3px;flex:none;}
.insk-trend-stat{padding:18px;margin-bottom:14px;}
.insk-trend-stat-v{margin-top:8px;font-size:30px;font-weight:800;letter-spacing:-1px;color:var(--c-ink);}
.insk-trend-stat-c{margin-top:5px;font-size:11.5px;font-weight:500;color:var(--c-textLight);}

/* ---- StackedShareBar ---- */
.insk-share{padding:16px 18px;margin-bottom:14px;}
.insk-share-bar{display:flex;height:14px;border-radius:7px;overflow:hidden;gap:2px;}
.insk-share-seg{height:100%;min-width:3px;}
.insk-share-legend{margin-top:12px;}
.insk-share-lrow{display:flex;align-items:center;gap:9px;padding:5px 0;font-size:12.5px;}
.insk-share-dot{width:10px;height:10px;border-radius:3px;flex:none;}
.insk-share-llabel{flex:1;min-width:0;font-weight:600;color:var(--c-ink);
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.insk-share-lval{flex:none;font-weight:700;color:var(--c-ink);}
.insk-share-lpct{flex:none;font-weight:600;color:var(--c-textLight);min-width:44px;text-align:end;}

/* ---- ProgressVsTarget ---- */
.insk-bullet{padding:15px 17px;margin-bottom:14px;}
.insk-bullet-head{display:flex;align-items:baseline;justify-content:space-between;gap:10px;margin-bottom:8px;}
.insk-bullet-v{font-size:14px;font-weight:800;color:var(--c-ink);}
.insk-bullet-track{position:relative;height:10px;border-radius:5px;
  background:color-mix(in srgb,var(--c-ink) 7%,transparent);}
.insk-bullet-fill{height:100%;border-radius:5px;background:var(--c-green);}
.insk-bullet-tick{position:absolute;top:-3px;bottom:-3px;inset-inline-end:0;width:2px;
  border-radius:1px;background:var(--c-ink);opacity:.55;}
.insk-bullet-pct{margin-top:7px;font-size:11.5px;font-weight:600;color:var(--c-textLight);}

/* ---- NarrativeCard ---- */
.insk-narr{padding:16px 18px;margin-bottom:14px;}
.insk-narr-head{display:flex;align-items:center;gap:8px;margin-bottom:9px;}
.insk-narr-head svg{width:15px;height:15px;color:var(--c-purple);}
.insk-narr-body{font-size:13px;font-weight:500;color:var(--c-text);line-height:1.6;}
.insk-narr-body p{margin:0 0 8px;}
.insk-narr-body p:last-child{margin-bottom:0;}
.insk-narr-body strong{font-weight:800;color:var(--c-ink);}
.insk-narr-cap{margin-top:9px;font-size:10.5px;font-weight:600;letter-spacing:.4px;
  text-transform:uppercase;color:var(--c-muted);}

/* ---- states / skeleton ---- */
.insk-state{padding:36px 24px;text-align:center;margin-bottom:14px;}
.insk-state-ic{width:52px;height:52px;border-radius:18px;margin:0 auto 14px;
  display:inline-flex;align-items:center;justify-content:center;
  background:color-mix(in srgb,var(--c-green) 12%,transparent);color:var(--c-greenDark);}
.insk-state-ic svg{width:24px;height:24px;}
.insk-state-ic.insk-err{background:color-mix(in srgb,var(--c-red) 10%,transparent);color:var(--c-red);}
.insk-state-t1{font-size:15px;font-weight:800;color:var(--c-ink);}
.insk-state-t2{margin-top:5px;font-size:12.5px;font-weight:500;color:var(--c-textLight);line-height:1.5;}
.insk-retry{margin-top:16px;min-height:44px;padding:0 22px;border:none;border-radius:14px;
  background:var(--c-green);color:var(--c-white);font-family:inherit;
  font-size:13px;font-weight:700;cursor:pointer;transition:transform .15s ease;}
.insk-retry:active{transform:scale(.96);}
.insk-skel{border-radius:24px;margin-bottom:14px;
  background:linear-gradient(90deg,
    color-mix(in srgb,var(--c-ink) 6%,transparent) 25%,
    color-mix(in srgb,var(--c-ink) 11%,transparent) 50%,
    color-mix(in srgb,var(--c-ink) 6%,transparent) 75%);
  background-size:200% 100%;animation:inskShimmer 1.5s infinite;}
@keyframes inskShimmer{0%{background-position:200% 0;}100%{background-position:-200% 0;}}
.insk-skel-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:14px;}
.insk-skel-grid .insk-skel{margin-bottom:0;}
.insk-narr-shimmer .insk-skel{border-radius:8px;margin-bottom:8px;}
.insk-narr-shimmer .insk-skel:last-child{margin-bottom:0;}

@media (prefers-reduced-motion: reduce){
  .insk-skel{animation:none;}
  .insk-chip,.insk-chip:active,.insk-retry,.insk-retry:active{transition:none;transform:none;}
}
`;

let cssInjected = false;
function ensureCss() {
    if (cssInjected || typeof document === 'undefined') return;
    const tag = document.createElement('style');
    tag.id = 'insk-css';
    tag.textContent = CSS;
    document.head.appendChild(tag);
    cssInjected = true;
}

const ICON = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
};

/* ------------------------------------------------------------------ */
/* Chart series palette — the four approved tokens, cycled.            */
/* (--c-blue is excluded: it aliases --c-green in the current theme.)  */
/* ------------------------------------------------------------------ */
export const CHART_COLORS = [
    'var(--c-green)',
    'var(--c-teal)',
    'var(--c-amber)',
    'var(--c-purple)',
];

/* ------------------------------------------------------------------ */
/* normaliseKpi — absorbs every reporting_v2 KPI inconsistency ONCE.   */
/*                                                                     */
/* Input: one element of an endpoint's kpis[] (the _build_kpi shape    */
/* plus per-endpoint extras). Output:                                  */
/*   {                                                                 */
/*     name,                                                           */
/*     value,          number, or the raw string for text KPIs         */
/*     previousValue,  number | null                                   */
/*     isText,         string-valued KPI (Peak Hour) → no delta ever   */
/*     isPercent,      true via ANY of: is_percentage, unit:'%',       */
/*                     '%' in the name (the /costs convention)         */
/*     invert,         down-is-good flag (COGS, Opex, Concentration)   */
/*     delta: null | { amount, kind, direction, good }                 */
/*       null when change_percent is null (previous 0/None) or isText  */
/*       amount    abs magnitude                                       */
/*       kind      'percent' | 'points' — 'points' only for the        */
/*                 /financial "Profit Margin" KPI, whose               */
/*                 change_percent is hand-built as curr − prev         */
/*       direction 'up' | 'down' | 'flat'                              */
/*       good      boolean | null (flat) — invert already applied      */
/*   }                                                                 */
/* ------------------------------------------------------------------ */
export function normaliseKpi(kpi) {
    const name = (kpi && kpi.name) || '';
    const rawValue = kpi ? kpi.value : null;
    const isText = typeof rawValue === 'string';
    const invert = !!(kpi && kpi.invert);
    const isPercent = !isText && !!(
        (kpi && kpi.is_percentage === true)
        || (kpi && kpi.unit === '%')
        || name.includes('%')
        // /financial's "Rev vs Target" is a percent emitted BARE — no
        // is_percentage, no unit, no '%' in the name. Fourth convention.
        || name === 'Rev vs Target'
    );

    let delta = null;
    const change = kpi ? kpi.change_percent : null;
    if (!isText && change != null && Number.isFinite(Number(change))) {
        const c = Number(change);
        let direction = 'flat';
        if (c > 0) direction = 'up';
        else if (c < 0) direction = 'down';
        const good = direction === 'flat' ? null : (direction === 'up') !== invert;
        // Backend quirk: /financial "Profit Margin" builds change_percent by
        // hand as (curr − prev) margin POINTS, not a relative % change. The
        // structural corroborant: for that KPI alone change_percent and
        // change_absolute are the same quantity at different rounding, so the
        // second clause keeps this working if the name literal ever changes.
        const kind = (
            name === 'Profit Margin'
            || (kpi.is_percentage === true && kpi.change_absolute != null
                && Math.abs(c - Number(kpi.change_absolute)) < 0.05)
        ) ? 'points' : 'percent';
        delta = { amount: Math.abs(c), kind, direction, good };
    }

    const previousValue = (kpi && kpi.previous_value != null && !isText)
        ? Number(kpi.previous_value)
        : (kpi && typeof kpi.previous_value === 'string' ? kpi.previous_value : null);

    return {
        name,
        value: isText ? rawValue : Number(rawValue || 0),
        previousValue,
        isText,
        isPercent,
        invert,
        delta,
    };
}

/* The one currency convention: symbol + 2dp locale grouping, and for
   negatives the sign comes BEFORE the symbol ('-£3,450.00') — the hero form
   from commit e793ce3, made kit-wide by the W2 final review (M3). Never
   '£-3,450.00'. */
export function fmtMoney(v, currency) {
    if (v == null) return currency + '0';
    const n = Number(v);
    const abs = Math.abs(n).toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
    return `${n < 0 ? '-' : ''}${currency}${abs}`;
}

/* Compact formatter for large figures — '£1.2M' / '£234.6K'. Values under
   1,000 fall through to the full form (fmtMoney with a currency, plain
   locale string without). Negatives are sign-first like fmtMoney (M3). */
export function fmtCompact(v, currency = '') {
    const n = Number(v) || 0;
    const abs = Math.abs(n);
    const sign = n < 0 ? '-' : '';
    if (abs >= 1e6) return `${sign}${currency}${(abs / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `${sign}${currency}${(abs / 1e3).toFixed(1)}K`;
    return currency ? fmtMoney(n, currency) : n.toLocaleString();
}

/* KPI tiles are ~144px wide on a 390pt phone; full-form money truncates
   mid-digit ('£1,234,56…') beyond roughly this magnitude, so fmtKpiValue
   auto-compacts from here. */
const COMPACT_AT = 100000;

/* Formats a normalised KPI's headline value.
   opts: {
     currency,
     format: 'currency' | 'number' | 'percent',   // percent = explicit
              escape hatch for percents the norm didn't auto-detect
     formatValue?: (norm) => string,              // full override — wins over
              everything, for '3.2 units/hr'-style KPIs
   }
   isPercent/isText override the format hint; values ≥100k auto-compact
   ('£1.2M') so the 2-up grid never truncates mid-digit. */
export function fmtKpiValue(norm, { currency = '', format = 'number', formatValue } = {}) {
    if (typeof formatValue === 'function') return formatValue(norm);
    if (norm.isText) return norm.value;
    if (norm.isPercent || format === 'percent') return `${Number(norm.value).toFixed(1)}%`;
    const n = Number(norm.value);
    if (format === 'currency') return fmtMoneyAuto(n, currency);
    return Math.abs(n) >= COMPACT_AT ? fmtCompact(n, '') : n.toLocaleString();
}

/* Money with the SAME auto-compaction rule as the KPI tiles: full form
   below COMPACT_AT (100k), compact at/above. Use this wherever a figure
   that also renders as a KPI tile appears again on the same screen (share
   legends, target bullets) — one screen must never state the same number
   two ways ('£84.0K' in a tile, '£84,000.00' two cards below) (M4). */
export function fmtMoneyAuto(v, currency = '') {
    const n = Number(v) || 0;
    return Math.abs(n) >= COMPACT_AT ? fmtCompact(n, currency) : fmtMoney(n, currency);
}

/* ------------------------------------------------------------------ */
/* mapAnomaly — adapts a /narrative anomalies[] row for <InsightList>. */
/* Anomalies are {metric, value, date, percentile, type, message} —    */
/* NO title/description — so piping them into InsightList raw gives    */
/* correctly-tinted rows with blank text. The message is the only      */
/* human-readable slot; types are 'positive' | 'warning', which        */
/* InsightList already maps (positive → success).                      */
/* ------------------------------------------------------------------ */
export function mapAnomaly(anomaly) {
    if (!anomaly) return null;
    return {
        title: anomaly.message || '',
        type: anomaly.type || 'info',
    };
}

/* Zero-fills INTERIOR gaps in a daily trend across the observed span.
   gold.daily_summary — like gold.hourly_sales (Task 6's finding) — only
   has rows for dates where a transaction occurred; the ETL does not
   zero-fill (etl/gold_aggregation.py). TrendChart spaces points evenly by
   array index, so an unfilled closed day would be silently smoothed into
   a continuous line — a factual misrepresentation. Promoted into the kit
   as the shared home for gap-filling (second consumer after Task 6's
   screen-local hourly variant).
   rows:     [{ [dateKey]: 'YYYY-MM-DD'(-ish), ... }]
   blankAt:  (dateStr) => row — builds the zero row for a missing day
   Edges are NOT padded (trimming a range's silent lead-in/out is fine;
   collapsing an interior gap is not). Stepping is done at LOCAL NOON via
   setDate — never new Date('YYYY-MM-DD') (parses as UTC) — so a DST
   change can neither skip nor repeat a calendar day. */
export function fillDailyGaps(rows, blankAt, dateKey = 'sales_date') {
    const byDate = new Map();
    (rows || []).forEach((r) => {
        const raw = r && r[dateKey];
        const key = typeof raw === 'string' ? raw.slice(0, 10) : null;
        if (key && /^\d{4}-\d{2}-\d{2}$/.test(key)) byDate.set(key, r);
    });
    if (byDate.size === 0) return [];
    const keys = Array.from(byDate.keys()).sort();
    const last = keys[keys.length - 1];
    const cursor = new Date(`${keys[0]}T12:00:00`);
    if (Number.isNaN(cursor.getTime())) return keys.map((k) => byDate.get(k));
    const out = [];
    // Safety bound: the widest preset (This Year) spans ≤366 days; malformed
    // spans can never loop unbounded.
    const MAX_DAYS = 400;
    for (let i = 0; i < MAX_DAYS; i += 1) {
        const key = toLocalISO(cursor);
        out.push(byDate.get(key) || blankAt(key));
        if (key >= last) break;
        cursor.setDate(cursor.getDate() + 1);
    }
    return out;
}

/* Zero-fills INTERIOR gaps in an hourly trend across the observed span.
   gold.hourly_sales — the source behind /financial's hourly trend AND
   /operations' sections.hourly — only has rows for hours where a
   transaction occurred; the ETL does not zero-fill
   (etl/gold_aggregation.py). TrendChart spaces points evenly by array
   index, so an unfilled gap is silently erased: a lunch closure renders
   as continuous trade. Filling interior hours with zeros makes the gap a
   real dip; the empty edges of the day stay trimmed (a shop open
   08:00-16:00 doesn't need hours 17-23 padded — that really is noise).
   Written by W2 Task 6 inside FinancialPerformanceScreen; promoted here
   by W2 Task 9 (Operational Efficiency), the second hourly consumer —
   the trigger the fillDailyGaps note named. */
export function fillHourlyGaps(rows) {
    const byHour = new Map();
    (rows || []).forEach((r) => {
        const h = Number(r && r.hour);
        if (Number.isFinite(h)) byHour.set(h, r);
    });
    if (byHour.size === 0) return [];
    const hours = Array.from(byHour.keys());
    const minH = Math.min(...hours);
    const maxH = Math.max(...hours);
    const out = [];
    for (let h = minH; h <= maxH; h += 1) {
        out.push(byHour.get(h) || { hour: h, revenue: 0, transactions: 0 });
    }
    return out;
}

/* ------------------------------------------------------------------ */
/* RangeBar — preset chip strip + resolved local-time range label.     */
/* The chip strip is the ONE approved horizontal-scroll exception.     */
/* props: { value: presetId, onChange(id, {start, end}), now?: Date }  */
/* onChange also receives the resolved local-time range so surfaces    */
/* never re-derive it (rangeForPreset + toLocalISO in four places).    */
/* ------------------------------------------------------------------ */
export function RangeBar({ value, onChange, now }) {
    ensureCss();
    const { t, language } = useLanguage();
    const range = useMemo(() => {
        try {
            return rangeForPreset(value, now || new Date());
        } catch (e) {
            // A stale persisted preset id (e.g. 'custom') must degrade to a
            // chips-only strip, not crash the whole surface.
            return null;
        }
    }, [value, now]);
    return (
        <div className="insk-rangebar">
            <div className="insk-chips" role="group" aria-label={t('date_range', {}, 'Date range')}>
                {PRESETS.map((p) => {
                    const active = p.id === value;
                    return (
                        <button
                            key={p.id}
                            type="button"
                            className={`insk-chip${active ? ' insk-chip-active' : ''}`}
                            aria-pressed={active}
                            onClick={() => onChange(p.id, rangeForPreset(p.id, now || new Date()))}
                        >
                            {active && (
                                <svg {...ICON} strokeWidth={2.6}><path d="M20 6L9 17l-5-5" /></svg>
                            )}
                            {t(p.labelKey, {}, p.fallback)}
                        </button>
                    );
                })}
            </div>
            {range && (
                <div className="insk-range-label insk-num">
                    {formatRangeLabel(range.start, range.end, language)}
                </div>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* DeltaPill — THE delta pill, shared by KpiGrid tiles and the         */
/* surfaces' hero cards (Tasks 6-9 all open with hero + delta), so the */
/* two can never diverge.                                              */
/* props: { norm } — normaliseKpi() output.                            */
/*   norm.isText        → renders nothing (string KPIs have no delta)  */
/*   norm.delta == null → muted "—" pill (previous 0/None — NEVER      */
/*                        "0%")                                        */
/*   otherwise          → arrow + magnitude; good/bad/flat treatment   */
/*                        (invert already applied by normaliseKpi);    */
/*                        kind 'points' renders the pts suffix         */
/* ------------------------------------------------------------------ */
export function DeltaPill({ norm }) {
    ensureCss();
    const { t } = useLanguage();
    if (!norm || norm.isText) return null;
    const d = norm.delta;
    if (d == null) {
        return <span className="insk-delta insk-delta-none insk-num">—</span>;
    }
    const cls = d.direction === 'flat'
        ? 'insk-delta-flat'
        : (d.good ? 'insk-delta-good' : 'insk-delta-bad');
    const suffix = d.kind === 'points'
        ? ` ${t('kpi_points_suffix', {}, 'pts')}`
        : '%';
    return (
        <span className={`insk-delta ${cls} insk-num`}>
            {d.direction === 'up' && (
                <svg {...ICON} strokeWidth={2.2}><path d="M7 17L17 7" /><path d="M9 7h8v8" /></svg>
            )}
            {d.direction === 'down' && (
                <svg {...ICON} strokeWidth={2.2}><path d="M7 7l10 10" /><path d="M17 9v8H9" /></svg>
            )}
            {d.amount.toFixed(1)}{suffix}
        </span>
    );
}

/* ------------------------------------------------------------------ */
/* HeroValue — THE hero card every Insights surface opens with (W2      */
/* final review I2: it was hand-copied five times, and commit e793ce3   */
/* already had to fix the negative sign in all five). One primitive —   */
/* W3/W4 surfaces open with this, never a copied block.                 */
/* props: {                                                             */
/*   label,     translated heading (surfaces own the t() call)          */
/*   norm,      normaliseKpi() output — value, previousValue, delta;    */
/*              null → renders nothing                                  */
/*   currency,  symbol string (rendered as the <sup>)                   */
/*   testid,    card testid; the value div gets `${testid}-value`       */
/*   children,  optional extra line(s) between value and compare —      */
/*              e.g. Product Detail's category line (.insk-hero-sub)    */
/* }                                                                    */
/* Anatomy: label + DeltaPill row → big value (sign BEFORE the currency */
/* sup for negatives, grouped int part, 2dp dec sup) → children →       */
/* "vs {previous} previous" compare line when previousValue exists.     */
/* ------------------------------------------------------------------ */
export function HeroValue({ label, norm, currency = '', testid, children }) {
    ensureCss();
    const { t } = useLanguage();
    if (!norm) return null;
    const value = Number(norm.value) || 0;
    const neg = value < 0;
    const [intPart, decPart] = Math.abs(value).toFixed(2).split('.');
    return (
        <GlassCard veil className="insk-hero" data-testid={testid}>
            <div className="insk-hero-row">
                <span className="insk-label">{label}</span>
                <DeltaPill norm={norm} />
            </div>
            <div
                className="insk-hero-big insk-num"
                data-testid={testid ? `${testid}-value` : undefined}
            >
                {neg && '-'}
                <sup>{currency}</sup>
                {Number(intPart).toLocaleString()}
                <sup className="insk-hero-dec">.{decPart}</sup>
            </div>
            {children}
            {norm.previousValue != null && (
                <div className="insk-hero-compare insk-num">
                    {t(
                        'kpi_vs_previous',
                        { amount: fmtMoney(norm.previousValue, currency) },
                        'vs {amount} previous',
                    )}
                </div>
            )}
        </GlassCard>
    );
}

/* ------------------------------------------------------------------ */
/* KpiGrid — 2-up glass KPI tiles.                                     */
/* props: { items, currency }                                          */
/*   items: [{ kpi, label, format: 'currency'|'number'|'percent',      */
/*             formatValue?: (norm) => string,                         */
/*             span?: number,     // grid columns, e.g. 2 = full width */
/*             caption?: true | string }]                              */
/*   caption true → auto "vs {previous} previous"                      */
/* ------------------------------------------------------------------ */
export function KpiGrid({ items = [], currency = '' }) {
    ensureCss();
    const { t } = useLanguage();
    return (
        <div className="insk-kpis">
            {items.map((item, i) => {
                const norm = normaliseKpi(item.kpi);
                const fmtOpts = { currency, format: item.format, formatValue: item.formatValue };
                const valueText = fmtKpiValue(norm, fmtOpts);

                // DeltaPill renders null for text KPIs itself, but the pill
                // slot must stay null-valued here so a text KPI with no
                // caption produces NO .insk-kpi-foot wrapper (DOM parity
                // with the pre-extraction markup).
                const pill = norm.isText ? null : <DeltaPill norm={norm} />;

                let caption = null;
                if (typeof item.caption === 'string') {
                    caption = item.caption;
                } else if (item.caption === true && !norm.isText && norm.previousValue != null) {
                    const prevNorm = { ...norm, value: norm.previousValue };
                    caption = t(
                        'kpi_vs_previous',
                        { amount: fmtKpiValue(prevNorm, fmtOpts) },
                        'vs {amount} previous',
                    );
                }

                return (
                    <GlassCard
                        veil
                        className="insk-kpi"
                        key={`${item.label || ''}#${i}`}
                        data-testid={`kpi-tile-${i}`}
                        style={item.span ? { gridColumn: `span ${item.span}` } : undefined}
                    >
                        <div className="insk-kpi-k">{item.label}</div>
                        <div
                            className={`insk-kpi-v${norm.isText ? ' insk-kpi-v-text' : ''} insk-num`}
                        >
                            {valueText}
                        </div>
                        {(pill || caption) && (
                            <div className="insk-kpi-foot">
                                {pill}
                                {caption && <span className="insk-kpi-cap insk-num">{caption}</span>}
                            </div>
                        )}
                    </GlassCard>
                );
            })}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* InsightList — backend insights[] as glass rows with tinted icons.   */
/* props: { items: [{ title, description, type }] }                    */
/* Types: info | warning | success | opportunity. The /operations      */
/* endpoint also emits "positive" — mapped to the success treatment.   */
/* Unknown types fall back to info rather than rendering unstyled.     */
/* ------------------------------------------------------------------ */
const INSIGHT_TYPES = ['info', 'warning', 'success', 'opportunity'];
const INSIGHT_ALIASES = { positive: 'success' };

const INSIGHT_ICONS = {
    info: <svg {...ICON}><circle cx="12" cy="12" r="9" /><path d="M12 11v5" /><path d="M12 8h.01" /></svg>,
    warning: (
        <svg {...ICON}>
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <path d="M12 9v4" /><path d="M12 17h.01" />
        </svg>
    ),
    success: <svg {...ICON}><circle cx="12" cy="12" r="9" /><path d="M8.5 12.5l2.5 2.5 4.5-5" /></svg>,
    opportunity: <svg {...ICON}><path d="M23 6l-9.5 9.5-5-5L1 18" /><path d="M17 6h6v6" /></svg>,
};

export function InsightList({ items = [] }) {
    ensureCss();
    if (!items.length) return null;
    return (
        <GlassCard veil className="insk-ins-list">
            {items.map((ins, i) => {
                const aliased = INSIGHT_ALIASES[ins.type] || ins.type;
                const type = INSIGHT_TYPES.includes(aliased) ? aliased : 'info';
                return (
                    <div className={`insk-ins-row insk-ins-${type}`} key={i} data-testid="insight-row">
                        <span className="insk-ins-ic">{INSIGHT_ICONS[type]}</span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                            <span className="insk-ins-t1" style={{ display: 'block' }}>{ins.title}</span>
                            {ins.description && (
                                <span className="insk-ins-t2 insk-num" style={{ display: 'block' }}>
                                    {ins.description}
                                </span>
                            )}
                        </span>
                    </div>
                );
            })}
        </GlassCard>
    );
}

/* ------------------------------------------------------------------ */
/* BarList — the ranking workhorse. Horizontal bars, value labels      */
/* ALWAYS visible (AAA — never hover-only), ≤maxRows then "+N more"    */
/* (a real 44pt disclosure button, not a dead label). Bars grow from   */
/* the inline-start edge, so RTL comes free.                           */
/* props: { items, formatValue?, maxRows=15, title?, onItemPress?,     */
/*          itemProps? }                                               */
/*   items: [{ label, value, pct?, caption?, color? }]                 */
/*   onItemPress?(item, index) — added in W2 Task 7: when provided,    */
/*   every row renders as a real 44pt <button> (list → detail push).   */
/*   itemProps?(item, index) => object — extra props spread onto the   */
/*   row element (W2 final review I5). The press seam alone cannot     */
/*   express a DISCLOSURE: rows that toggle an in-place panel need     */
/*   aria-expanded (+ aria-controls when open) or they announce as     */
/*   plain buttons. Use this for any per-row a11y state.               */
/* ------------------------------------------------------------------ */
export function BarList({ items = [], formatValue, maxRows = 15, title, onItemPress, itemProps }) {
    ensureCss();
    const { t } = useLanguage();
    const [expanded, setExpanded] = useState(false);
    // Reset expansion when the list itself changes (e.g. a range switch) so
    // the ≤maxRows rule re-applies to the new data — otherwise an expand on
    // "This Week" silently leaks 40 rows into "This Year".
    const itemsKey = items.map((it) => it.label).join('\u0001');
    const [prevItemsKey, setPrevItemsKey] = useState(itemsKey);
    if (itemsKey !== prevItemsKey) {
        setPrevItemsKey(itemsKey);
        setExpanded(false);
    }
    if (!items.length) return null;
    const fmt = formatValue || ((v) => Number(v).toLocaleString());
    const visible = expanded ? items : items.slice(0, maxRows);
    const hidden = items.length - visible.length;
    const max = Math.max(...items.map((it) => Number(it.value) || 0), 0);
    return (
        <GlassCard veil className="insk-bars">
            {title && <div className="insk-label" style={{ marginBottom: 10 }}>{title}</div>}
            {visible.map((item, i) => {
                const w = max > 0 ? (Math.max(Number(item.value) || 0, 0) / max) * 100 : 0;
                const Row = onItemPress ? 'button' : 'div';
                return (
                    <Row
                        className="insk-bar-row"
                        key={`${item.label || ''}#${i}`}
                        data-testid="bar-row"
                        {...(onItemPress
                            ? { type: 'button', onClick: () => onItemPress(item, i) }
                            : {})}
                        {...(itemProps ? itemProps(item, i) : {})}
                    >
                        <div className="insk-bar-head">
                            <span className="insk-bar-label">{item.label}</span>
                            <span className="insk-bar-value insk-num">
                                {fmt(item.value)}
                                {item.pct != null && (
                                    <span className="insk-bar-pct"> · {Number(item.pct).toFixed(1)}%</span>
                                )}
                            </span>
                        </div>
                        <div className="insk-bar-track">
                            <div
                                className="insk-bar-fill"
                                style={{ width: `${w}%`, background: item.color || 'var(--c-green)' }}
                            />
                        </div>
                        {item.caption && <div className="insk-bar-cap insk-num">{item.caption}</div>}
                    </Row>
                );
            })}
            {hidden > 0 && (
                <button
                    type="button"
                    className="insk-bar-more insk-num"
                    data-testid="bar-more"
                    onClick={() => setExpanded(true)}
                >
                    {t('bar_list_more', { count: hidden }, '+{count} more')}
                </button>
            )}
            {expanded && items.length > maxRows && (
                <button
                    type="button"
                    className="insk-bar-more insk-num"
                    data-testid="bar-less"
                    onClick={() => setExpanded(false)}
                >
                    {t('bar_list_less', {}, 'Show less')}
                </button>
            )}
        </GlassCard>
    );
}

/* Trend plot geometry — shared by path building and the zero baseline. */
const TREND_W = 322;
const TREND_H = 120;
const TREND_PAD_TOP = 10;
const TREND_PAD_BOTTOM = 12;

/* Catmull-Rom → cubic bezier smoothing — HomeTab's sparkPaths approach.
   yFor maps a data value into the padded plot box (real min/max domain). */
function trendLinePath(values, yFor) {
    const n = values.length;
    if (n < 2) return null;
    const pts = values.map((v, i) => [(i / (n - 1)) * TREND_W, yFor(v)]);
    let line = `M ${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
    for (let i = 0; i < n - 1; i += 1) {
        const p0 = pts[i - 1] || pts[i];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2] || p2;
        const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
        const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
        line += ` C ${c1[0].toFixed(1)} ${c1[1].toFixed(1)}, ${c2[0].toFixed(1)} ${c2[1].toFixed(1)}, ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
    }
    return { line, end: pts[n - 1] };
}

let trendGradientSeq = 0;

/* ------------------------------------------------------------------ */
/* TrendChart — hand-authored SVG line/area chart.                     */
/* Chart rule: a line chart needs ≥4 points to be justified; below     */
/* that this renders a stat card instead (total + point count — the    */
/* FIRST series when multi-series).                                    */
/* Domain is [min(0, data)..max(0, data)] — negative values (loss      */
/* weeks, net_profit) plot inside the box with a dashed zero baseline  */
/* instead of clipping below it.                                       */
/* The chart block is dir="ltr": a time axis stays chronological       */
/* left→right even in RTL locales.                                     */
/* props: {                                                            */
/*   points: [{label, value}],          // single series, OR:          */
/*   series: [{name, points, color?}],  // multi-series (lines +       */
/*            legend, colours cycled from CHART_COLORS)                */
/*   title, ariaLabel, formatValue?,                                   */
/*   markerIndex?: int                  // W2 Task 9: marks ONE point  */
/*            of the PRIMARY (first) series — amber dashed guide +     */
/*            emphasised dot (testid trend-marker). For "the peak      */
/*            hour visibly marked"-style callouts. Out-of-range /      */
/*            non-integer values are ignored; the <4-point stat-card   */
/*            fallback never renders it. Name the marked value in      */
/*            ariaLabel — SVG internals are aria-opaque.               */
/* }                                                                   */
/* ------------------------------------------------------------------ */
export function TrendChart({ points = [], series, title, ariaLabel, formatValue, markerIndex }) {
    ensureCss();
    const { t } = useLanguage();
    // Stable per-mount gradient id — two TrendCharts on one surface must
    // not share defs ids.
    const gradId = useMemo(() => {
        trendGradientSeq += 1;
        return `inskTrendArea${trendGradientSeq}`;
    }, []);
    const fmt = formatValue || ((v) => Number(v).toLocaleString());

    const isMultiInput = !!(series && series.length);
    const seriesList = (isMultiInput ? series : (points.length ? [{ name: title, points }] : []))
        .map((s, i) => ({
            name: s.name,
            color: s.color || (isMultiInput ? CHART_COLORS[i % CHART_COLORS.length] : 'var(--c-green)'),
            points: s.points || [],
            values: (s.points || []).map((p) => Number(p.value) || 0),
        }))
        .filter((s) => s.points.length > 0);

    if (!seriesList.length) return null;

    const primary = seriesList[0];
    const n = primary.points.length;
    const total = primary.values.reduce((s, v) => s + v, 0);

    /* <4 points → a chart is not justified; render the stat instead. */
    if (n < 4) {
        return (
            <GlassCard veil className="insk-trend-stat" data-testid="trend-stat">
                <div className="insk-label">{title}</div>
                <div className="insk-trend-stat-v insk-num">{fmt(total)}</div>
                <div className="insk-trend-stat-c insk-num">
                    {t('trend_points_count', { count: n }, '{count} data points')}
                </div>
            </GlassCard>
        );
    }

    const multi = seriesList.length > 1;
    const all = seriesList.reduce((acc, s) => acc.concat(s.values), []);
    const min = Math.min(0, ...all);
    let max = Math.max(0, ...all);
    if (max === min) max = min + 1; // all-zero guard
    const yFor = (v) => TREND_PAD_TOP
        + (1 - (v - min) / (max - min)) * (TREND_H - TREND_PAD_TOP - TREND_PAD_BOTTOM);
    const yZero = yFor(0);
    const paths = seriesList.map((s) => ({ ...s, spark: trendLinePath(s.values, yFor) }));
    const area = !multi && paths[0].spark
        ? `${paths[0].spark.line} L ${TREND_W} ${yZero.toFixed(1)} L 0 ${yZero.toFixed(1)} Z`
        : null;

    /* Optional single-point marker on the primary series (peak-hour-style
       callouts). Invalid indexes are ignored, never a crash. */
    const mIdx = Number.isInteger(markerIndex) && markerIndex >= 0 && markerIndex < n
        ? markerIndex
        : null;
    const mX = mIdx != null ? (mIdx / (n - 1)) * TREND_W : 0;
    const mY = mIdx != null ? yFor(primary.values[mIdx]) : 0;

    const midIdx = Math.floor((n - 1) / 2);
    return (
        <GlassCard veil className="insk-trend">
            <div className="insk-trend-head">
                <span className="insk-label">{title}</span>
                {!multi && <span className="insk-trend-total insk-num">{fmt(total)}</span>}
            </div>
            <div dir="ltr">
                <svg
                    className="insk-trend-svg"
                    viewBox={`0 0 ${TREND_W} ${TREND_H}`}
                    role="img"
                    aria-label={ariaLabel || title}
                >
                    {area && (
                        <defs>
                            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="var(--c-green)" stopOpacity="0.30" />
                                <stop offset="100%" stopColor="var(--c-green)" stopOpacity="0.02" />
                            </linearGradient>
                        </defs>
                    )}
                    <line x1="0" y1="30" x2={TREND_W} y2="30" stroke="var(--c-ink)" strokeOpacity="0.05" />
                    <line x1="0" y1="60" x2={TREND_W} y2="60" stroke="var(--c-ink)" strokeOpacity="0.05" />
                    <line x1="0" y1="90" x2={TREND_W} y2="90" stroke="var(--c-ink)" strokeOpacity="0.05" />
                    {min < 0 && (
                        <line
                            x1="0"
                            y1={yZero.toFixed(1)}
                            x2={TREND_W}
                            y2={yZero.toFixed(1)}
                            stroke="var(--c-ink)"
                            strokeOpacity="0.28"
                            strokeDasharray="3 3"
                            data-testid="trend-zero"
                        />
                    )}
                    {area && <path d={area} fill={`url(#${gradId})`} />}
                    {paths.map((s, i) => s.spark && (
                        <g key={`${s.name || ''}#${i}`}>
                            <path d={s.spark.line} stroke={s.color} strokeWidth="2.5" strokeLinecap="round" fill="none" />
                            <circle cx={s.spark.end[0]} cy={s.spark.end[1]} r={multi ? 3 : 4} fill={s.color} />
                            {!multi && (
                                <circle cx={s.spark.end[0]} cy={s.spark.end[1]} r="8" fill={s.color} opacity="0.15" />
                            )}
                        </g>
                    ))}
                    {mIdx != null && (
                        <g data-testid="trend-marker">
                            <line
                                x1={mX.toFixed(1)}
                                y1={TREND_PAD_TOP}
                                x2={mX.toFixed(1)}
                                y2={TREND_H - TREND_PAD_BOTTOM}
                                stroke="var(--c-amber)"
                                strokeOpacity="0.55"
                                strokeWidth="1.5"
                                strokeDasharray="3 3"
                            />
                            <circle cx={mX.toFixed(1)} cy={mY.toFixed(1)} r="8" fill="var(--c-amber)" opacity="0.18" />
                            <circle cx={mX.toFixed(1)} cy={mY.toFixed(1)} r="4" fill="var(--c-amber)" />
                        </g>
                    )}
                </svg>
                <div className="insk-trend-x insk-num">
                    <span>{primary.points[0].label}</span>
                    {midIdx > 0 && midIdx < n - 1 && (
                        <span
                            className="insk-trend-xmid"
                            style={{ left: `${(midIdx / (n - 1)) * 100}%` }}
                        >
                            {primary.points[midIdx].label}
                        </span>
                    )}
                    <span>{primary.points[n - 1].label}</span>
                </div>
            </div>
            {multi && (
                <div className="insk-trend-legend" data-testid="trend-legend">
                    {seriesList.map((s, i) => (
                        <span className="insk-trend-lg" key={`${s.name || ''}#${i}`}>
                            <span
                                className="insk-trend-lgdot"
                                aria-hidden="true"
                                style={{ background: s.color }}
                            />
                            {s.name}
                        </span>
                    ))}
                </div>
            )}
        </GlassCard>
    );
}

/* ------------------------------------------------------------------ */
/* StackedShareBar — the part-to-whole view. Replaces the web pages'   */
/* pie charts (pie is a11y grade C and fails on phone — divergence     */
/* pre-approved). One 100% stacked strip + a legend that ALWAYS        */
/* carries the numeric values and shares.                              */
/* props: { segments: [{label, value, color?}], ariaLabel,             */
/*          formatValue?, title?, denominator? }                       */
/* denominator (W2 Task 8 fix round): forces the LEGEND percentage     */
/* base. Zero/negative segments are dropped from the DRAWING, which    */
/* silently shifts the default base to the drawn positive total — in   */
/* a loss period (/costs: COGS+OpEx > revenue, net dropped) that       */
/* prints shares of total spend right beside prose quoting shares of   */
/* revenue. Passing denominator={revenue} keeps every stated           */
/* percentage on the caller's base; bar geometry still splits the      */
/* strip by the drawn total (identical proportions either way). A      */
/* missing/zero/non-finite denominator falls back to the drawn total   */
/* — fully additive.                                                   */
/* ------------------------------------------------------------------ */
export function StackedShareBar({ segments = [], ariaLabel, formatValue, title, denominator }) {
    ensureCss();
    const positive = segments.filter((s) => (Number(s.value) || 0) > 0);
    const total = positive.reduce((s, seg) => s + Number(seg.value), 0);
    if (total <= 0) return null;
    const den = Number(denominator);
    const pctBase = Number.isFinite(den) && den > 0 ? den : total;
    const fmt = formatValue || ((v) => Number(v).toLocaleString());
    return (
        <GlassCard veil className="insk-share">
            {title && <div className="insk-label" style={{ marginBottom: 10 }}>{title}</div>}
            {/* aria-label falls back to title — role="img" must never ship
                without an accessible name (WCAG 1.1.1). */}
            <div className="insk-share-bar" role="img" aria-label={ariaLabel || title} data-testid="share-bar">
                {positive.map((seg, i) => (
                    <div
                        key={`${seg.label || ''}#${i}`}
                        className="insk-share-seg"
                        style={{
                            width: `${(Number(seg.value) / total) * 100}%`,
                            background: seg.color || CHART_COLORS[i % CHART_COLORS.length],
                        }}
                    />
                ))}
            </div>
            <div className="insk-share-legend">
                {positive.map((seg, i) => (
                    <div className="insk-share-lrow" key={`${seg.label || ''}#${i}`} data-testid="share-legend-row">
                        <span
                            className="insk-share-dot"
                            aria-hidden="true"
                            style={{ background: seg.color || CHART_COLORS[i % CHART_COLORS.length] }}
                        />
                        <span className="insk-share-llabel">{seg.label}</span>
                        <span className="insk-share-lval insk-num">{fmt(seg.value)}</span>
                        <span className="insk-share-lpct insk-num">
                            {((Number(seg.value) / pctBase) * 100).toFixed(1)}%
                        </span>
                    </div>
                ))}
            </div>
        </GlassCard>
    );
}

/* ------------------------------------------------------------------ */
/* ProgressVsTarget — bullet-style KPI-vs-target bar. Value AND % of   */
/* target render as TEXT (never colour-only). Fill caps at 100%; the   */
/* text reports the real overshoot. No positive target → renders       */
/* nothing (the /financial endpoint omits the KPI in that case too).   */
/* props: { label, value, target, formatValue? }                       */
/* ------------------------------------------------------------------ */
export function ProgressVsTarget({ label, value, target, formatValue }) {
    ensureCss();
    const { t } = useLanguage();
    if (!(Number(target) > 0)) return null;
    const fmt = formatValue || ((v) => Number(v).toLocaleString());
    // Guard NaN (value undefined/junk) and clamp negatives to 0 — a bullet
    // bar must never render "NaN% of target" or a negative fill width.
    const safeValue = Number.isFinite(Number(value)) ? Number(value) : 0;
    const pct = Math.max((safeValue / Number(target)) * 100, 0);
    return (
        <GlassCard veil className="insk-bullet" data-testid="progress-target">
            <div className="insk-bullet-head">
                <span className="insk-label">{label}</span>
                <span className="insk-bullet-v insk-num">{fmt(safeValue)}</span>
            </div>
            <div className="insk-bullet-track">
                <div className="insk-bullet-fill" style={{ width: `${Math.min(pct, 100)}%` }} />
                <div className="insk-bullet-tick" aria-hidden="true" />
            </div>
            <div className="insk-bullet-pct insk-num">
                {t('pct_of_target', { pct: pct.toFixed(1) }, '{pct}% of target')}
            </div>
        </GlassCard>
    );
}

/* Parses the backend's naive-UTC "YYYY-MM-DD HH:MM:SS.ffffff" string.
   str(datetime.utcnow()) carries NO timezone marker — parsing it as-is
   would read it as LOCAL time and skew the freshness caption by the UTC
   offset. Normalise: space→T, trim microseconds to ms, append Z. */
function parseGeneratedAt(raw) {
    if (!raw) return null;
    let s = String(raw).trim().replace(' ', 'T');
    s = s.replace(/(\.\d{3})\d+/, '$1');
    if (!/([zZ]|[+-]\d{2}:?\d{2})$/.test(s)) s += 'Z';
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
}

/* Renders **bold** spans as real <strong> elements — the safe replacement
   for the web pages' dangerouslySetInnerHTML markdown. Only **bold** is
   supported; anything else stays literal text. */
function renderBold(text) {
    const parts = String(text).split(/\*\*(.+?)\*\*/g);
    return parts.map((part, i) => (
        i % 2 === 1 ? <strong key={i}>{part}</strong> : part
    ));
}

/* ------------------------------------------------------------------ */
/* NarrativeCard — the AI narrative block. An ENHANCEMENT: loading     */
/* shimmer / parsed text / NOTHING on failure — its failure must never */
/* break the surface. Freshness caption from generated_at.             */
/* props: { state: 'loading'|'ready'|'error', text, generatedAt,       */
/*          title? }                                                   */
/* ------------------------------------------------------------------ */
export function NarrativeCard({ state, text, generatedAt, title }) {
    ensureCss();
    const { t } = useLanguage();

    if (state === 'error') return null;
    if (state === 'ready' && !text) return null;
    if (state !== 'loading' && state !== 'ready') return null;

    const head = (
        <div className="insk-narr-head">
            <svg {...ICON} strokeWidth={1.8}>
                <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
                <path d="M19 15l.9 2.1L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.9z" />
            </svg>
            <span className="insk-label">{title || t('ai_summary', {}, 'AI Summary')}</span>
        </div>
    );

    if (state === 'loading') {
        return (
            <GlassCard veil className="insk-narr">
                {head}
                <div className="insk-narr-shimmer" data-testid="narrative-loading" aria-hidden="true">
                    <div className="insk-skel" style={{ height: 12, width: '95%' }} />
                    <div className="insk-skel" style={{ height: 12, width: '88%' }} />
                    <div className="insk-skel" style={{ height: 12, width: '60%' }} />
                </div>
            </GlassCard>
        );
    }

    const paragraphs = String(text).split(/\n+/).map((p) => p.trim()).filter(Boolean);
    const when = parseGeneratedAt(generatedAt);
    let freshness = null;
    if (when) {
        const mins = Math.floor((Date.now() - when.getTime()) / 60000);
        if (mins < 1) freshness = t('updated_just_now', {}, 'Updated just now');
        else if (mins < 60) freshness = t('updated_mins_ago', { mins }, 'Updated {mins}m ago');
        else if (mins < 1440) freshness = t('updated_hours_ago', { hours: Math.floor(mins / 60) }, 'Updated {hours}h ago');
        else freshness = t('updated_days_ago', { days: Math.floor(mins / 1440) }, 'Updated {days}d ago');
    }

    return (
        <GlassCard veil className="insk-narr">
            {head}
            {/* No insk-num here — tabular figures are for aligned columns of
                numerals, not running prose. */}
            <div className="insk-narr-body" data-testid="narrative-body">
                {paragraphs.map((p, i) => <p key={i}>{renderBold(p)}</p>)}
            </div>
            {freshness && (
                <div className="insk-narr-cap insk-num" data-testid="narrative-updated">{freshness}</div>
            )}
        </GlassCard>
    );
}

/* ------------------------------------------------------------------ */
/* SurfaceSkeleton — the loading shimmer for a full Insights surface:  */
/* chip strip → 2×2 KPI grid → chart → list. Pure decoration.          */
/* ------------------------------------------------------------------ */
export function SurfaceSkeleton() {
    ensureCss();
    return (
        <div data-testid="surface-skeleton" aria-hidden="true">
            <div className="insk-skel" style={{ height: 44, borderRadius: 999 }} />
            <div className="insk-skel-grid">
                <div className="insk-skel" style={{ height: 96 }} />
                <div className="insk-skel" style={{ height: 96 }} />
                <div className="insk-skel" style={{ height: 96 }} />
                <div className="insk-skel" style={{ height: 96 }} />
            </div>
            <div className="insk-skel" style={{ height: 180 }} />
            <div className="insk-skel" style={{ height: 150 }} />
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* SurfaceStates — the loading / error+retry / empty / ready triad.    */
/* This is the state machine NONE of the web reporting pages actually  */
/* ships (they swallow errors or silently blank); every native surface */
/* wraps its content in this instead.                                  */
/* props: { status: 'loading'|'error'|'empty'|'ready', onRetry,        */
/*          emptyTitle?, emptyMsg?, children }                          */
/* ------------------------------------------------------------------ */
export function SurfaceStates({ status, onRetry, emptyTitle, emptyMsg, children }) {
    ensureCss();
    const { t } = useLanguage();

    if (status === 'loading') return <SurfaceSkeleton />;

    if (status === 'error') {
        return (
            <GlassCard veil className="insk-state" data-testid="surface-error">
                <span className="insk-state-ic insk-err">
                    <svg {...ICON}>
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 8v4" />
                        <path d="M12 16h.01" />
                    </svg>
                </span>
                <div className="insk-state-t1">
                    {t('insights_error_title', {}, "Couldn't load this report")}
                </div>
                <div className="insk-state-t2">
                    {t('insights_error_msg', {}, 'Check your connection and try again.')}
                </div>
                <button type="button" className="insk-retry" onClick={onRetry}>
                    {t('retry', {}, 'Retry')}
                </button>
            </GlassCard>
        );
    }

    if (status === 'empty') {
        return (
            <GlassCard veil className="insk-state" data-testid="surface-empty">
                <span className="insk-state-ic">
                    <svg {...ICON} strokeWidth={1.75}>
                        <path d="M3 21h18" />
                        <path d="M6 21v-9" />
                        <path d="M11 21V7" />
                        <path d="M16 21v-9" />
                        <path d="M21 21V4" />
                    </svg>
                </span>
                <div className="insk-state-t1">
                    {emptyTitle || t('insights_empty_title', {}, 'No data for this period')}
                </div>
                <div className="insk-state-t2">
                    {emptyMsg || t('insights_empty_msg', {}, 'Try a different date range.')}
                </div>
            </GlassCard>
        );
    }

    return <>{children}</>;
}
